import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/apiClient";
import { useAuth } from "@/lib/AuthContext";

/**
 * ViewMode = the mode (live | test) THIS desktop session wants to view.
 * Per-user, stored in localStorage. Sent as `X-CD-Mode` header on every request
 * (see apiClient.js). The backend honors the header per-request and never
 * persists it, so other users (mobile app, etc.) are unaffected.
 */
const ViewModeContext = createContext(null);

const KEY = (userId) => `cd_view_mode_${userId}`;

export function ViewModeProvider({ children }) {
  const { user } = useAuth();
  const [viewMode, setViewModeState] = useState("live");
  const [globalMode, setGlobalMode] = useState(null);
  const [globalMeta, setGlobalMeta] = useState(null);
  const [loaded, setLoaded] = useState(false);

  // Hydrate the per-user override synchronously the moment user.id is known.
  // This avoids a race where pages fire their initial fetch (with the default
  // 'live' header) before the async /system/mode call completes.
  useEffect(() => {
    if (!user?.id) return;
    let stored = null;
    try { stored = localStorage.getItem(KEY(user.id)); }
    catch (err) { console.warn("ViewMode: localStorage unavailable", err); }
    const hydrated = (stored === "live" || stored === "test") ? stored : "live";
    window.__cdActiveMode = hydrated;
    setViewModeState(hydrated);
    // Notify AuthContext to re-validate credentials against the (possibly
    // preview) backend for the hydrated mode.
    try {
      window.dispatchEvent(new CustomEvent("cd:mode-change", { detail: { mode: hydrated } }));
    } catch (err) { /* noop */ }
  }, [user?.id]);

  // Async refresh of global meta (read-only display info, not used for mode resolution).
  useEffect(() => {
    if (!user?.id) {
      setLoaded(true);
      return;
    }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/system/mode");
        if (!alive) return;
        setGlobalMode(data?.mode || "live");
        setGlobalMeta(data || null);
      } catch (err) {
        if (!alive) return;
        console.warn("ViewMode: failed to fetch /system/mode", err?.response?.status || err);
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [user?.id]);

  // Persist + propagate user-selected mode. The apiClient interceptor reads
  // the live value from window.__cdActiveMode on every request.
  const setViewMode = useCallback((mode) => {
    if (mode !== "live" && mode !== "test") return;
    if (user?.id) {
      window.__cdActiveMode = mode;
      try {
        localStorage.setItem(KEY(user.id), mode);
      } catch (err) {
        console.warn("ViewMode: failed to persist preference", err);
      }
    }
    setViewModeState(mode);
    // Notify auth + other listeners so they can re-fetch with the new
    // backend's credentials. Fires only when the mode actually changes
    // (see `viewMode !== mode` guard would be nice, but a redundant event
    // is harmless).
    try {
      window.dispatchEvent(new CustomEvent("cd:mode-change", { detail: { mode } }));
    } catch (err) { /* noop */ }
  }, [user?.id]);

  useEffect(() => {
    window.__cdActiveMode = viewMode;
  }, [viewMode]);

  const refreshGlobal = useCallback(async () => {
    try {
      const { data } = await api.get("/system/mode");
      setGlobalMode(data?.mode || "live");
      setGlobalMeta(data || null);
    } catch (err) {
      console.warn("ViewMode: refresh failed", err?.response?.status || err);
    }
  }, []);

  // Stable provider value
  const value = useMemo(
    () => ({ viewMode, setViewMode, globalMode, globalMeta, loaded, refreshGlobal }),
    [viewMode, setViewMode, globalMode, globalMeta, loaded, refreshGlobal]
  );

  return <ViewModeContext.Provider value={value}>{children}</ViewModeContext.Provider>;
}

export const useViewMode = () => useContext(ViewModeContext);

export function clearStoredViewMode(userId) {
  try {
    if (userId) localStorage.removeItem(KEY(userId));
  } catch (err) {
    console.warn("clearStoredViewMode failed", err);
  }
  window.__cdActiveMode = undefined;
}
